import { useState } from "react"
import type { ConciergeAccessState } from "./adapter"

interface Props {
  wifiName: string
  wifiPassword: string
  doorCode: string
  doorCodeVisibility: "available_now" | "available_on_arrival" | "locked" | "not_configured"
  doorCodePolicyText?: string
  accessState: ConciergeAccessState
  onUnlock?: () => void
}

const WifiIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <circle cx="12" cy="20" r="1" fill="currentColor" />
  </svg>
)

const DoorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="2" width="18" height="20" rx="2" />
    <path d="M15 11h.01" strokeWidth="2.5" />
  </svg>
)

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

function copyToClipboard(text: string) {
  if (navigator.clipboard) return navigator.clipboard.writeText(text)
  const el = document.createElement("textarea")
  el.value = text
  document.body.appendChild(el)
  el.select()
  document.execCommand("copy")
  document.body.removeChild(el)
  return Promise.resolve()
}

export function EssentialsSection({ wifiName, wifiPassword, doorCode, doorCodeVisibility, doorCodePolicyText, accessState, onUnlock }: Props) {
  const [wifiRevealed, setWifiRevealed] = useState(false)
  const [doorRevealed, setDoorRevealed] = useState(false)
  const [wifiCopied, setWifiCopied] = useState(false)
  const [doorCopied, setDoorCopied] = useState(false)

  const isAuthenticated = accessState === "authenticated"
  const isIcalLocked = accessState === "ical-locked"
  const isPublic = accessState === "public"

  function handleCopy(value: string, setCopied: (v: boolean) => void) {
    void copyToClipboard(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  if (isPublic && !wifiName && doorCodeVisibility === "not_configured") {
    return null
  }

  const showLockBanner = (isPublic || isIcalLocked) && Boolean(wifiPassword || doorCode || doorCodeVisibility === "available_now")

  if (showLockBanner) {
    return (
      <div className="gc-essentials">
        <div className="gc-lock-banner">
          <div className="gc-lock-banner__icon"><LockIcon /></div>
          <div>
            <div className="gc-lock-banner__title">Stay details protected</div>
            <div className="gc-lock-banner__desc">
              {doorCodePolicyText || "Verify your booking to access WiFi and door code."}
            </div>
          </div>
        </div>
        {onUnlock && (
          <button className="gc-unlock-btn" onClick={onUnlock} type="button">
            <LockIcon /> Unlock stay details
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="gc-essentials">
      {/* WiFi */}
      {wifiName && (
        <div className="gc-card">
          <div className="gc-essential-item">
            <div className="gc-essential-item__left">
              <div className={`gc-essential-item__icon${isAuthenticated ? " gc-essential-item__icon--unlock" : ""}`}>
                <WifiIcon />
              </div>
              <div>
                <div className="gc-essential-item__label">WiFi</div>
                <div className="gc-essential-item__value">{wifiName}</div>
                {isAuthenticated && wifiPassword && (
                  <div className="gc-essential-item__value">
                    {wifiRevealed ? wifiPassword : <span className="gc-essential-item__value--masked">••••••••</span>}
                  </div>
                )}
                {!isAuthenticated && (
                  <div className="gc-essential-item__value--muted">Password protected</div>
                )}
              </div>
            </div>
            {isAuthenticated && wifiPassword && (
              <div className="gc-essential-item__actions">
                <button
                  className="gc-icon-btn"
                  aria-label={wifiRevealed ? "Hide password" : "Reveal password"}
                  onClick={() => setWifiRevealed(v => !v)}
                  type="button"
                >
                  {wifiRevealed ? <EyeOffIcon /> : <EyeIcon />}
                </button>
                <button
                  className={`gc-icon-btn${wifiCopied ? " gc-icon-btn--copied" : ""}`}
                  aria-label="Copy password"
                  onClick={() => handleCopy(wifiPassword, setWifiCopied)}
                  type="button"
                >
                  {wifiCopied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Door Code */}
      {doorCodeVisibility !== "not_configured" && (
        <div className="gc-card">
          <div className="gc-essential-item">
            <div className="gc-essential-item__left">
              <div className={`gc-essential-item__icon${isAuthenticated && doorCodeVisibility === "available_now" ? " gc-essential-item__icon--unlock" : ""}`}>
                <DoorIcon />
              </div>
              <div>
                <div className="gc-essential-item__label">Door Code</div>
                {isAuthenticated && doorCodeVisibility === "available_now" && doorCode ? (
                  <div className="gc-essential-item__value">
                    {doorRevealed ? doorCode : <span className="gc-essential-item__value--masked">••••••</span>}
                  </div>
                ) : doorCodeVisibility === "available_on_arrival" ? (
                  <div className="gc-essential-item__value--muted">Available at check-in</div>
                ) : (
                  <div className="gc-essential-item__value--muted">Protected</div>
                )}
              </div>
            </div>
            {isAuthenticated && doorCodeVisibility === "available_now" && doorCode && (
              <div className="gc-essential-item__actions">
                <button
                  className="gc-icon-btn"
                  aria-label={doorRevealed ? "Hide code" : "Reveal code"}
                  onClick={() => setDoorRevealed(v => !v)}
                  type="button"
                >
                  {doorRevealed ? <EyeOffIcon /> : <EyeIcon />}
                </button>
                <button
                  className={`gc-icon-btn${doorCopied ? " gc-icon-btn--copied" : ""}`}
                  aria-label="Copy door code"
                  onClick={() => handleCopy(doorCode, setDoorCopied)}
                  type="button"
                >
                  {doorCopied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
